import React, { useEffect } from 'react';
import { Form, Input, Select, DatePicker, Switch, Button, Space, Divider } from 'antd';
import dayjs from 'dayjs';
import { CrmInteractionDTO, InteractionType } from '../types';
import { UserSelect } from '@/modules/auth/components/UserSelect';
import PlatformSelector from '@/modules/customer-notification/components/common/PlatformSelector';
import NotificationTemplateSelector from '@/modules/customer-notification/components/common/NotificationTemplateSelector';
import { NotificationConfigurationSelector } from '@/modules/action-flow/config/components/editor/NotificationConfigurationSelector';
import { Languages, NotificationPlatforms } from '@/shared/api/model';

const { TextArea } = Input;
const { Option } = Select;

interface Props {
    initialValues?: Partial<CrmInteractionDTO>;
    custNo?: string;
    isSubmitting?: boolean;
    onSubmit: (values: CrmInteractionDTO) => void;
    onCancel: () => void;
}

const INTERACTION_TYPE_LABELS: Record<InteractionType, string> = {
    CALL: 'Phone Call',
    MEETING: 'Meeting',
    VISIT: 'Site Visit',
    EMAIL: 'Email',
    NOTE: 'Note',
};

const InteractionForm: React.FC<Props> = ({ initialValues, custNo, isSubmitting, onSubmit, onCancel }) => {
    const [form] = Form.useForm();

    useEffect(() => {
        if (initialValues) {
            form.setFieldsValue({
                ...initialValues,
                interactionDate: initialValues.interactionDate ? dayjs(initialValues.interactionDate) : dayjs(),
                followUpDate: initialValues.followUpDate ? dayjs(initialValues.followUpDate) : undefined,
                notificationConfigurations: initialValues.notificationConfigCode
                    ? [{ code: initialValues.notificationConfigCode, name: initialValues.notificationConfigCode, platform: initialValues.notificationPlatform || '', active: true }]
                    : [],
            });
        } else {
            form.setFieldsValue({
                custNo: custNo || '',
                type: 'CALL',
                interactionDate: dayjs(),
                followUpDone: false,
                sendNotification: false,
                notificationLanguage: Languages.EN,
            });
        }
    }, [initialValues, custNo, form]);

    const handleFinish = (values: any) => {
        const selectedConfiguration = values.notificationConfigurations?.[0];
        const notificationPlatform = values.notificationPlatform || selectedConfiguration?.platform;
        onSubmit({
            ...values,
            custNo: values.custNo || custNo,
            interactionDate: values.interactionDate?.format('YYYY-MM-DD'),
            followUpDate: values.followUpDate?.format('YYYY-MM-DD'),
            notificationPlatform,
            notificationLanguage: values.notificationLanguage,
            notificationTemplateName: values.notificationTemplateName,
            notificationConfigCode: selectedConfiguration?.code,
        });
    };

    const sendNotification = Form.useWatch('sendNotification', form);
    const notificationPlatform = Form.useWatch('notificationPlatform', form) as NotificationPlatforms | undefined;

    return (
        <Form form={form} layout="vertical" onFinish={handleFinish}>
            {!custNo && (
                <Form.Item name="custNo" label="Customer No." rules={[{ required: true }]}>
                    <Input placeholder="e.g. 000001" />
                </Form.Item>
            )}

            <div className="grid grid-cols-2 gap-4">
                <Form.Item name="type" label="Interaction Type" rules={[{ required: true }]}>
                    <Select>
                        {(Object.keys(INTERACTION_TYPE_LABELS) as InteractionType[]).map(t => (
                            <Option key={t} value={t}>{INTERACTION_TYPE_LABELS[t]}</Option>
                        ))}
                    </Select>
                </Form.Item>
                <Form.Item name="interactionDate" label="Date" rules={[{ required: true }]}>
                    <DatePicker className="w-full" />
                </Form.Item>
            </div>

            <Form.Item name="subject" label="Subject" rules={[{ required: true }]}>
                <Input placeholder="Brief description of the interaction" />
            </Form.Item>

            <Form.Item
                name="officer"
                label="Officer"
                rules={sendNotification ? [{ required: true, message: 'Select an officer to notify' }] : undefined}
            >
                <UserSelect placeholder="Search and select officer" style={{ width: '100%' }} />
            </Form.Item>

            <Form.Item name="notes" label="Notes">
                <TextArea rows={4} placeholder="Detailed notes about this interaction..." />
            </Form.Item>

            <Form.Item name="outcome" label="Outcome">
                <Input placeholder="Result or conclusion of the interaction" />
            </Form.Item>

            <div className="grid grid-cols-2 gap-4">
                <Form.Item name="followUpDate" label="Follow-up Date">
                    <DatePicker className="w-full" />
                </Form.Item>
                <Form.Item name="followUpDone" label="Follow-up Done" valuePropName="checked">
                    <Switch />
                </Form.Item>
            </div>

            <Divider>Officer Notification</Divider>

            <Form.Item name="sendNotification" label="Send Notification" valuePropName="checked">
                <Switch />
            </Form.Item>

            {sendNotification && (
                <>
                    <div className="grid grid-cols-2 gap-4">
                        <Form.Item name="notificationPlatform" label="Platform" rules={[{ required: true, message: 'Select a platform' }]}>
                            <PlatformSelector />
                        </Form.Item>
                        <Form.Item name="notificationLanguage" label="Language">
                            <Select>
                                <Option value={Languages.EN}>English</Option>
                                <Option value={Languages.SW}>Swahili</Option>
                            </Select>
                        </Form.Item>
                    </div>

                    <Form.Item name="notificationTemplateName" label="Template">
                        <NotificationTemplateSelector platform={notificationPlatform} />
                    </Form.Item>

                    <Form.Item name="notificationConfigurations" label="Notification Configuration">
                        <NotificationConfigurationSelector maxCount={1} showPreview={false} />
                    </Form.Item>
                </>
            )}

            <div className="flex justify-end">
                <Space>
                    <Button onClick={onCancel}>Cancel</Button>
                    <Button type="primary" htmlType="submit" loading={isSubmitting}>
                        {initialValues?.id ? 'Update' : 'Log Interaction'}
                    </Button>
                </Space>
            </div>
        </Form>
    );
};

export default InteractionForm;
